(() => {
  "use strict";

  // Prevent double-execution if trykk.js is included multiple times
  if (window.__TRYKK_LOADED__ === true) return;
  window.__TRYKK_LOADED__ = true;

  // =========================================================
  // TRYKK.JS (CLEAN v5 PROD)
  //
  // Works for:
  // - SparkLayer (B2B): resolves selected Shopify variant -> variant SKU via VariantInventory JSON,
  //   then adds via spark.updateCart({ products:[{ sku, quantity, ...}] })
  // - Shopify (B2C): adds via /cart/add.js with variantId directly
  //
  // Key:
  // - We DO NOT use spark.getVariant() (not supported in your Spark environment)
  // - We map variantId -> SKU (best) or fallback to option-text matching via VariantInventory
  // - If we cannot resolve a SKU for the selected variant in Spark mode: hard fail (safer than wrong variant)
  //
  // Debug:
  // - Enable with ?trykkdbg=1 or localStorage.TRYKKDBG=1
  // =========================================================

  const ROOT_SEL = "[data-trykk-root]";
  const MOUNT_SEL = "[data-trykk-mount]";
  const CFG_SEL = "script[data-trykk-config]";
  const BOX_SEL = "[data-trykk-box]";

  const TAG = "[TRYKK]";
  const log = (...a) => console.log(TAG, ...a);
  const warn = (...a) => console.warn(TAG, ...a);
  const errlog = (...a) => console.error(TAG, ...a);

  function dbgEnabled() {
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.get("trykkdbg") === "1") return true;
      if (localStorage.getItem("TRYKKDBG") === "1") return true;
    } catch (_) {}
    return false;
  }

  const norm = (s) => String(s || "").trim();
  const normLC = (s) => norm(s).toLowerCase();

  // ---------------------------------------------------------
  // Dawn/OS2 helpers
  // ---------------------------------------------------------
  function findQtyInput(formId) {
    if (formId) {
      const q = document.querySelector(`input[name="quantity"][form="${CSS.escape(formId)}"]`);
      if (q) return q;
    }
    const mainProduct = document.querySelector("product-info");
    if (mainProduct) {
      const q2 = mainProduct.querySelector('input.quantity__input[name="quantity"]');
      if (q2) return q2;
    }
    return (
      document.querySelector('input.quantity__input[name="quantity"]') ||
      document.querySelector('input[name="quantity"]')
    );
  }

  function findMainForm(formId) {
    if (formId) {
      const f = document.getElementById(formId);
      if (f) return f;
    }
    return document.querySelector('form[action*="/cart/add"]') || document.querySelector("product-form form");
  }

  function getQty(qtyInput) {
    const v = parseInt(qtyInput?.value || "0", 10);
    return Number.isFinite(v) ? v : 0;
  }

  function readVariantIdFromFormStrict(form) {
    if (!form) return 0;

    const hidden = form.querySelector('input[name="id"][type="hidden"]');
    if (hidden?.value) {
      const v = parseInt(hidden.value, 10);
      if (Number.isFinite(v) && v > 0) return v;
    }

    const sel = form.querySelector('select[name="id"]');
    if (sel?.value) {
      const v = parseInt(sel.value, 10);
      if (Number.isFinite(v) && v > 0) return v;
    }

    const any = form.querySelector('[name="id"]');
    if (any?.value) {
      const v = parseInt(any.value, 10);
      if (Number.isFinite(v) && v > 0) return v;
    }

    return 0;
  }

  // ---------------------------------------------------------
  // VariantInventory caching (per page/product)
  // ---------------------------------------------------------
  const __invCache = {
    productId: "",
    arr: null,
    byVariantId: null,
    allowedSkus: null
  };

  function getCurrentProductId() {
    const pi = document.querySelector("product-info[data-product-id]");
    return norm(pi?.dataset?.productId);
  }

  function loadVariantInventoryCache() {
    const pid = getCurrentProductId();
    if (!pid) return null;

    if (__invCache.productId === pid && __invCache.arr) return __invCache;

    const el = document.getElementById(`VariantInventory-${pid}`);
    if (!el) return null;

    let arr = null;
    try {
      const parsed = JSON.parse(el.textContent || "[]");
      arr = Array.isArray(parsed) ? parsed : null;
    } catch {
      arr = null;
    }
    if (!arr) return null;

    const byVariantId = new Map();
    const allowedSkus = new Set();

    // Schema-flexible variant id extraction
    const getEid = (e) => {
      const cand = [
        e.variantId, e.variant_id, e.shopifyVariantId, e.shopify_variant_id,
        e.id, e.variantID, e.VariantId, e.VariantID, e.Variant_id,
        e.ShopifyVariantId, e.ShopifyVariantID
      ].map(norm).filter(Boolean);

      for (const c of cand) if (/^\d+$/.test(c)) return Number(c);
      return 0;
    };

    for (const e of arr) {
      const sku = norm(e?.sku);
      if (sku) allowedSkus.add(sku);

      const eid = getEid(e);
      if (eid && sku && !byVariantId.has(eid)) byVariantId.set(eid, sku);
    }

    __invCache.productId = pid;
    __invCache.arr = arr;
    __invCache.byVariantId = byVariantId;
    __invCache.allowedSkus = allowedSkus;

    if (dbgEnabled()) {
      log("VariantInventory cache loaded", {
        productId: pid,
        entries: arr.length,
        idsMapped: byVariantId.size,
        skus: allowedSkus.size
      });
    }

    return __invCache;
  }

  // Read selected option texts from Dawn <variant-selects> fieldsets/selects
  function getSelectedOptionsFromVariantSelects() {
    const opts = [];

    const vs = document.querySelector("variant-selects");
    const scope = vs || (document.querySelector("product-info") || document);

    // Fieldset pills: input:checked + label text
    scope.querySelectorAll("fieldset").forEach((fs) => {
      const checked = fs.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
      if (!checked) return;

      let val = "";
      if (checked.id) {
        const lab = fs.querySelector(`label[for="${CSS.escape(checked.id)}"]`);
        val = norm(lab?.textContent);
      }
      if (!val) val = norm(checked.value);

      val = val.split("\n")[0].trim();
      if (val) opts.push(val);
    });

    // Selects (fallback)
    scope.querySelectorAll("select").forEach((sel) => {
      if (sel.name === "quantity") return;
      const o = sel.selectedOptions?.[0];
      let val = norm(o?.textContent || sel.value);
      val = val.split("\n")[0].trim();
      if (val) opts.push(val);
    });

    return [...new Set(opts.filter(Boolean))];
  }

  function scoreInventoryEntry(entry, selectedLC) {
    const parts = [];

    [
      "option1","option2","option3","Option1","Option2","Option3",
      "size","Size","storrelse","Storrelse",
      "color","Color","farge","Farge",
      "title","Title","variantTitle","VariantTitle"
    ].forEach((k) => { if (entry && entry[k] != null) parts.push(normLC(entry[k])); });

    if (Array.isArray(entry?.options)) parts.push(...entry.options.map(normLC));
    if (Array.isArray(entry?.optionValues)) parts.push(...entry.optionValues.map(normLC));

    const joined = parts.filter(Boolean).join(" | ");
    if (!joined) return -1;

    let score = 0;
    for (const s of selectedLC) {
      if (!s) continue;
      if (joined.includes(s)) score += 10;
      if (s.length <= 4 && joined.split(/[^a-z0-9]+/i).includes(s)) score += 6;
    }
    return score;
  }

  function resolveSkuFromInventoryByOptions(cache) {
    if (!cache?.arr) return "";

    const selected = getSelectedOptionsFromVariantSelects();
    const selectedLC = selected.map(normLC).filter(Boolean);
    if (!selectedLC.length) return "";

    let bestSku = "";
    let bestScore = -1;

    for (const e of cache.arr) {
      const sku = norm(e?.sku);
      if (!sku) continue;
      const sc = scoreInventoryEntry(e, selectedLC);
      if (sc > bestScore) {
        bestScore = sc;
        bestSku = sku;
      }
    }

    // Min score gate (avoid wrong picks)
    if (bestScore < 10) return "";
    return bestSku;
  }

  function resolveSelectedVariantSkuStrict(form) {
    const cache = loadVariantInventoryCache();
    if (!cache) return "";

    const variantId = readVariantIdFromFormStrict(form);
    let sku = variantId ? (cache.byVariantId.get(Number(variantId)) || "") : "";

    if (!sku) sku = resolveSkuFromInventoryByOptions(cache);

    if (sku && cache.allowedSkus && !cache.allowedSkus.has(sku)) sku = "";
    return sku;
  }

  // ---------------------------------------------------------
  // Cart helpers
  // ---------------------------------------------------------
  function makeCustomAttributes(obj) {
    return Object.entries(obj || {})
      .filter(([, v]) => v !== undefined && v !== null && String(v) !== "")
      .map(([key, value]) => ({ key: String(key), value: String(value) }));
  }

  function hasSpark() {
    return !!window.spark &&
      typeof window.spark.updateCart === "function" &&
      typeof window.spark.getCart === "function";
  }

  async function getCartSafe() {
    return await window.spark.getCart();
  }

  function getAttrValue(customAttributes, key) {
    const hit = (customAttributes || []).find(a => a?.key === key);
    return hit?.value != null ? String(hit.value) : "";
  }

  function findItemBySku(cart, sku) {
    if (!cart?.items || !Array.isArray(cart.items)) return null;
    const s = norm(sku);
    if (!s) return null;
    return cart.items.find(it => norm(it?.sku) === s) || null;
  }

  function findTrykkItem(cart, trykkSku, mainVariantId, code) {
    if (!cart?.items || !Array.isArray(cart.items)) return null;
    const sku = norm(trykkSku);
    const forId = norm(mainVariantId);
    const c = norm(code);
    if (!sku || !forId || !c) return null;

    return cart.items.find(it => {
      if (norm(it?.sku) !== sku) return false;
      const src = getAttrValue(it.customAttributes, "_source");
      const tf = getAttrValue(it.customAttributes, "_trykk_for");
      const tc = getAttrValue(it.customAttributes, "_trykk_code");
      return src === "trykk_box" && tf === forId && tc === c;
    }) || null;
  }

  async function sparkUpdateCartProducts(products) {
    return await window.spark.updateCart({ products });
  }

  async function shopifyAddItems(items) {
    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ items })
    });

    if (!res.ok) {
      const t = await res.text().catch(() => "");
      throw new Error(`Shopify cart/add.js feilet (${res.status}): ${t}`);
    }
    return await res.json();
  }

  function triggerCartRefreshBestEffort() {
    try { window.dispatchEvent(new CustomEvent("cart:refresh")); } catch (_) {}
    try { document.dispatchEvent(new CustomEvent("cart:refresh")); } catch (_) {}
    try { window.dispatchEvent(new CustomEvent("cart:updated")); } catch (_) {}
    try { document.dispatchEvent(new CustomEvent("cart:updated")); } catch (_) {}
    try { window.dispatchEvent(new Event("cart:refresh")); } catch (_) {}
  }

  async function upsertMainAndTrykkManySpark({
    mainSku,
    qAdd,
    mainVariantId,
    mainPropsObj,
    trykkLines
  }) {
    const cartBefore = await getCartSafe();

    const mainExisting = findItemBySku(cartBefore, mainSku);
    const products = [];

    if (mainExisting?.itemKey) {
      products.push({
        itemKey: String(mainExisting.itemKey),
        quantity: Number(mainExisting.quantity || 0) + Number(qAdd)
      });
    } else {
      products.push({
        sku: String(mainSku),
        quantity: Number(qAdd),
        customAttributes: makeCustomAttributes(mainPropsObj)
      });
    }

    for (const t of (trykkLines || [])) {
      const existing = findTrykkItem(cartBefore, t.sku, mainVariantId, t.code);

      let mergedValuesJson = t.valuesJson || "";
      if (existing?.customAttributes && t.inputType !== "none") {
        const prev = getAttrValue(existing.customAttributes, "_trykk_values");
        if (prev) {
          try {
            const a = JSON.parse(prev);
            const b = JSON.parse(t.valuesJson || "[]");
            if (Array.isArray(a) && Array.isArray(b)) mergedValuesJson = JSON.stringify(a.concat(b));
          } catch (_) {}
        }
      }

      if (existing?.itemKey) {
        const nextQty = Number(existing.quantity || 0) + Number(t.qtyAdd || 0);

        products.push({
          itemKey: String(existing.itemKey),
          quantity: nextQty,
          customAttributes: makeCustomAttributes({
            _source: "trykk_box",
            _trykk_for: String(mainVariantId),
            _trykk_for_sku: String(mainSku),
            _trykk_code: String(t.code),
            _trykk_input_type: String(t.inputType || ""),
            _trykk_values: t.inputType === "none" ? "" : String(mergedValuesJson || "")
          })
        });
      } else {
        products.push({
          sku: String(t.sku),
          quantity: Number(t.qtyAdd || 0),
          customAttributes: makeCustomAttributes({
            _source: "trykk_box",
            _trykk_for: String(mainVariantId),
            _trykk_for_sku: String(mainSku),
            _trykk_code: String(t.code),
            _trykk_input_type: String(t.inputType || ""),
            _trykk_values: t.inputType === "none" ? "" : String(t.valuesJson || "")
          })
        });
      }
    }

    const results = await sparkUpdateCartProducts(products);
    try { if (typeof window.spark.openDrawer === "function") window.spark.openDrawer(); } catch (_) {}
    return results;
  }

  // ---------------------------------------------------------
  // JSON config & access logic (unchanged from your prod version)
  // ---------------------------------------------------------
  function parseConfig(root) {
    const el = root.querySelector(CFG_SEL);
    if (!el) return null;
    try { return JSON.parse(el.textContent || "{}"); }
    catch (e) { errlog("Invalid TRYKK JSON config", e); return null; }
  }

  function getUrlParams() {
    try { return new URL(window.location.href).searchParams; }
    catch (_) { return new URLSearchParams(); }
  }

  function findAccessByUrl(cfg) {
    const params = getUrlParams();
    const club = String(params.get("club") || "").trim();
    const trykk = String(params.get("trykk") || "").trim();

    const list = Array.isArray(cfg?.access_list) ? cfg.access_list : [];

    if (club) {
      const hit = list.find(a => a?.active === true && String(a?.url_key || "").trim().toLowerCase() === club.toLowerCase());
      if (hit) return hit;
    }

    if (trykk === "1" || trykk === "true" || trykk === "on") {
      const hit = list.find(a =>
        a?.active === true && (
          String(a?.url_key || "").trim().toLowerCase() === "trykk" ||
          String(a?.key || "").trim().toLowerCase() === "default"
        )
      );
      if (hit) return hit;
    }

    return null;
  }

  const TRYKK_UNLOCK_KEY = "TRYKK_UNLOCK_V1";

  function readTrykkUnlock() {
    try {
      const raw = localStorage.getItem(TRYKK_UNLOCK_KEY);
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (!obj || !obj.key || !obj.expiresAt) return null;

      if (Date.now() > Number(obj.expiresAt)) {
        localStorage.removeItem(TRYKK_UNLOCK_KEY);
        return null;
      }
      return { key: String(obj.key), expiresAt: Number(obj.expiresAt) };
    } catch (_) {
      return null;
    }
  }

  function findAccessByUnlock(cfg) {
    const u = readTrykkUnlock();
    if (!u?.key) return null;
    const list = Array.isArray(cfg?.access_list) ? cfg.access_list : [];
    return list.find(a => a?.active === true && String(a?.key || "").trim() === u.key.trim()) || null;
  }

  function filterOptionsByAccess(cfg, access) {
    const optionsAll = Array.isArray(cfg?.options) ? cfg.options : [];
    if (!access) return optionsAll;

    const allowedCodes = new Set(
      (Array.isArray(access?.options) ? access.options : [])
        .map(o => String(o?.kode || "").trim())
        .filter(Boolean)
    );

    if (allowedCodes.size === 0) return optionsAll;
    return optionsAll.filter(o => allowedCodes.has(String(o?.kode || "").trim()));
  }

  function normList(v) { return Array.isArray(v) ? v : []; }

  function splitCsv(str) {
    if (!str) return [];
    return String(str)
      .split(/[,|;]/g)
      .map(s => String(s).trim().toLowerCase())
      .filter(Boolean);
  }

  function intersect(a, b) {
    const sb = new Set((b || []).map(x => String(x).trim().toLowerCase()));
    for (const x of (a || [])) {
      if (sb.has(String(x).trim().toLowerCase())) return true;
    }
    return false;
  }

  function optionMatchesContext(opt, cfg) {
    if (!opt || opt.aktiv !== true) return false;

    const p = cfg?.page?.product || {};
    const c = cfg?.page?.customer || {};

    const productId = String(p.id || "");
    const productHandle = String(p.handle || "");
    const vendor = String(p.vendor || "").trim().toLowerCase();
    const tags = normList(p.tags).map(t => String(t).trim().toLowerCase()).filter(Boolean);
    const collections = normList(p.collections).map(x => String(x).trim()).filter(Boolean);

    const allowedVendors = splitCsv(opt.allowedvendors);
    if (allowedVendors.length > 0 && !allowedVendors.includes(vendor)) return false;

    const allowedCustomerTags = normList(opt.allowedcustomertags).map(t => String(t).trim().toLowerCase()).filter(Boolean);
    const customerTags = normList(c.tags).map(t => String(t).trim().toLowerCase()).filter(Boolean);
    if (allowedCustomerTags.length > 0) {
      if (c.is_logged_in !== true) return false;
      if (!intersect(allowedCustomerTags, customerTags)) return false;
    }

    const showAll = opt.show_all === true;

    const incProducts = normList(opt.include_products);
    const incCollections = normList(opt.include_collections);
    const incTags = normList(opt.include_product_tags).map(t => String(t).trim().toLowerCase()).filter(Boolean);
    const hasAnyInclude = incProducts.length || incCollections.length || incTags.length;

    const excProducts = normList(opt.exclude_products);
    const excCollections = normList(opt.exclude_collections);
    const excTags = normList(opt.exclude_product_tags).map(t => String(t).trim().toLowerCase()).filter(Boolean);

    const matchExcludeProduct = excProducts.some(x => String(x?.id || "") === productId || String(x?.handle || "") === productHandle);
    const matchExcludeCollection = excCollections.some(x => collections.includes(String(x?.handle || "")));
    const matchExcludeTag = excTags.some(t => tags.includes(t));
    if (matchExcludeProduct || matchExcludeCollection || matchExcludeTag) return false;

    if (showAll) return true;
    if (!hasAnyInclude) return false;

    const matchProduct = incProducts.some(x => String(x?.id || "") === productId || String(x?.handle || "") === productHandle);
    const matchCollection = incCollections.some(x => collections.includes(String(x?.handle || "")));
    const matchTag = incTags.some(t => tags.includes(t));
    return matchProduct || matchCollection || matchTag;
  }

  function groupAndSort(options) {
    const groups = new Map();
    for (const opt of options) {
      const pack = opt.trykk_pack || {};
      const pid = pack.id || "no-pack";
      if (!groups.has(pid)) groups.set(pid, { pack, options: [] });
      groups.get(pid).options.push(opt);
    }
    const list = Array.from(groups.values());
    list.sort((a, b) => Number(a.pack?.sortering || 0) - Number(b.pack?.sortering || 0));
    for (const g of list) g.options.sort((a, b) => Number(a.sortering || 0) - Number(b.sortering || 0));
    return list;
  }

  // ---------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------
  function el(tag, attrs = {}, children = []) {
    const n = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v === null || v === undefined) continue;
      if (k === "class") n.className = String(v);
      else if (k === "text") n.textContent = String(v);
      else n.setAttribute(k, String(v));
    }
    for (const ch of children) n.appendChild(ch);
    return n;
  }

  function clampInt(n, min, max) {
    const x = parseInt(String(n), 10);
    if (!Number.isFinite(x)) return min;
    return Math.max(min, Math.min(max, x));
  }

  function renderInputs(box, qty) {
    const inputType = String(box.dataset.inputType || "none").toLowerCase();
    const wrap = box.querySelector(".js-trykk-inputs");
    if (!wrap) return;

    wrap.innerHTML = "";
    if (inputType === "none") return;

    const maxLen = clampInt(box.dataset.maxLen || 0, 0, 200) || 0;
    const minV = clampInt(box.dataset.min || 0, -9999, 9999);
    const maxV = clampInt(box.dataset.max || 99, -9999, 9999);
    const rangeA = Math.min(minV, maxV);
    const rangeB = Math.max(minV, maxV);

    for (let i = 0; i < qty; i++) {
      const row = el("div", { class: "trykk-row" });
      const idx = el("div", { class: "trykk-idx", text: `${i + 1}.` });
      row.appendChild(idx);

      if (inputType === "text") {
        const inp = el("input", { type: "text", class: "js-trykk-unit" });
        if (maxLen > 0) inp.maxLength = maxLen;
        inp.placeholder = "Navn";
        inp.style.flex = "1";
        row.appendChild(inp);
        wrap.appendChild(row);
        continue;
      }

      if (inputType === "number_range") {
        const sel = el("select", { class: "js-trykk-unit" });
        sel.style.flex = "1";
        for (let n = rangeA; n <= rangeB; n++) {
          const o = document.createElement("option");
          o.value = String(n);
          o.textContent = String(n);
          sel.appendChild(o);
        }
        row.appendChild(sel);
        wrap.appendChild(row);
        continue;
      }

      if (inputType === "name_number") {
        const nameInp = el("input", { type: "text", class: "js-trykk-unit js-trykk-name" });
        if (maxLen > 0) nameInp.maxLength = maxLen;
        nameInp.placeholder = "Navn";
        nameInp.style.flex = "1";

        const sel = el("select", { class: "js-trykk-unit js-trykk-number" });
        sel.style.width = "120px";
        for (let n = rangeA; n <= rangeB; n++) {
          const o = document.createElement("option");
          o.value = String(n);
          o.textContent = String(n);
          sel.appendChild(o);
        }

        row.appendChild(nameInp);
        row.appendChild(sel);
        wrap.appendChild(row);
        continue;
      }

      wrap.appendChild(el("div", { text: "" }));
      return;
    }
  }

  function collectValues(box, qty) {
    const inputType = String(box.dataset.inputType || "none").toLowerCase();
    if (inputType === "none") return [];

    if (inputType === "name_number") {
      const rows = Array.from(box.querySelectorAll(".js-trykk-inputs > div.trykk-row"));
      const pairs = rows.map(row => ({
        name: String(row.querySelector(".js-trykk-name")?.value || "").trim(),
        number: String(row.querySelector(".js-trykk-number")?.value || "").trim()
      }));
      while (pairs.length < qty) pairs.push({ name: "", number: "" });
      if (pairs.length > qty) pairs.length = qty;
      return pairs;
    }

    const units = Array.from(box.querySelectorAll(".js-trykk-unit"));
    const values = units.map(elm => String(elm.value || "").trim());
    while (values.length < qty) values.push("");
    if (values.length > qty) values.length = qty;
    return values;
  }

  function validateValues(box, values) {
    const required = String(box.dataset.required || "false") === "true";
    const inputType = String(box.dataset.inputType || "none").toLowerCase();
    if (!required || inputType === "none") return { ok: true };

    if (inputType === "text") {
      for (let i = 0; i < values.length; i++) if (!values[i]) return { ok: false, msg: `Fyll ut navn på linje ${i + 1}.` };
      return { ok: true };
    }

    if (inputType === "number_range") {
      for (let i = 0; i < values.length; i++) if (values[i] === "") return { ok: false, msg: `Velg nummer på linje ${i + 1}.` };
      return { ok: true };
    }

    if (inputType === "name_number") {
      for (let i = 0; i < values.length; i++) {
        const v = values[i] || {};
        if (!String(v.name || "").trim()) return { ok: false, msg: `Fyll ut navn på linje ${i + 1}.` };
        if (!String(v.number || "").trim()) return { ok: false, msg: `Velg nummer på linje ${i + 1}.` };
      }
      return { ok: true };
    }

    return { ok: true };
  }


  function requiresFlikPrint(cfg, code, colorCode) {
    const customer = cfg?.page?.customer;
    const productId = String(cfg?.page?.product?.id || "");
    const option = (cfg?.options || []).find(o => String(o.kode) === "889293");
    return String(code) === "889293" &&
      customer?.is_logged_in === true &&
      String(customer.customer_number || "").trim() === "17679" &&
      (customer.tags || []).some(t => String(t).toLowerCase() === "flik_normalt_trykk") &&
      option?.aktiv === true &&
      (option.include_products || []).some(p => String(p.id) === productId) &&
      colorCode === "7929";
  }

  function selectedColorCode(root) {
    const selected = getSelectedOptionsFromVariantSelects();
    const color = selected.map(v => String(v).trim().match(/^(\d{4})(?:\b|[-_])/)).find(Boolean);
    if (color) return color[1];
    const cfg = parseConfig(root);
    const variantId = readVariantIdFromFormStrict(findMainForm());
    const variant = (cfg?.page?.product?.variant_skus || []).find(v => Number(v.id) === variantId);
    const sku = String(variant?.sku || "");
    return /^\d{10}/.test(sku) ? sku.slice(6, 10) : "";
  }

  function applyFlikPolicy(box) {
    const root = box.closest(ROOT_SEL);
    const enabled = box.querySelector(".js-trykk-enabled");
    if (!root || !enabled || String(box.dataset.trykkCode) !== "889293") return;
    const mandatory = requiresFlikPrint(parseConfig(root), box.dataset.trykkCode, selectedColorCode(root));
    if (mandatory && !box.__flikMandatory) box.__flikPreviousChecked = enabled.checked;
    if (mandatory) enabled.checked = true;
    else if (box.__flikMandatory) enabled.checked = !!box.__flikPreviousChecked;
    box.__flikMandatory = mandatory;
    enabled.disabled = mandatory;
    enabled.setAttribute("aria-disabled", String(mandatory));
    const label = enabled.nextElementSibling;
    if (label) label.textContent = mandatory ? "Obligatorisk trykk" : "Legg til";
  }

  function syncFlikPolicy() {
    document.querySelectorAll(BOX_SEL).forEach(box => {
      if (String(box.dataset.trykkCode) !== "889293") return;
      const enabled = box.querySelector(".js-trykk-enabled");
      const before = enabled?.checked;
      applyFlikPolicy(box);
      // Full sync re-renders inputs and wipes entered values, so only run it when the checkbox actually changed
      if (enabled && enabled.checked !== before) box.__trykkSync?.();
    });
  }

  function initBox(box, qtyInput) {
    if (box.__trykkInited) return;
    box.__trykkInited = true;

    const enabled = box.querySelector(".js-trykk-enabled");
    const hint = box.querySelector(".js-trykk-hint");
    const extra = box.querySelector(".js-trykk-extra");
    const qtyView = box.querySelector(".js-trykk-qty");
    if (!enabled) return;

    function sync() {
      applyFlikPolicy(box);
      const q = getQty(qtyInput);
      if (qtyView) qtyView.value = String(q);

      const inputType = String(box.dataset.inputType || "none").toLowerCase();

      if (enabled.checked) {
        if (hint) hint.textContent = `(følger antall: ${q})`;
        if (extra) {
          extra.style.display = "";
          if (inputType !== "none") renderInputs(box, q);
        }
      } else {
        if (hint) hint.textContent = "";
        if (extra) extra.style.display = "none";
        const wrap = box.querySelector(".js-trykk-inputs");
        if (wrap) wrap.innerHTML = "";
      }
    }

    const defaultOn = String(box.dataset.defaultOn || "false") === "true";
    box.__trykkSync = sync;
    enabled.checked = defaultOn;
    if (extra) extra.style.display = defaultOn ? "" : "none";
    sync();

    enabled.addEventListener("change", sync);
    qtyInput.addEventListener("input", sync);
    qtyInput.addEventListener("change", sync);
  }

  function formatTrykkPrice(v) {
    if (v === null || v === undefined) return "";
    if (typeof v === "string") return v.trim();

    if (typeof v === "number" && Number.isFinite(v)) {
      const amount = v / 100;
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "nb-NO", { style: "currency", currency: "NOK" }).format(amount);
      } catch (_) {
        return `${amount.toFixed(2)}`;
      }
    }
    return String(v).trim();
  }

  function render(root) {
    const cfg = parseConfig(root);
    const mount = root.querySelector(MOUNT_SEL);
    if (!mount) return;
    mount.innerHTML = "";
    if (!cfg) return;

    const access = findAccessByUrl(cfg) || findAccessByUnlock(cfg);
    const hasAccess = !!access;

    if (cfg.enabled !== true && !hasAccess) return;

    const optionsAll = filterOptionsByAccess(cfg, access);
    const optionsVisible = optionsAll.filter(opt => optionMatchesContext(opt, cfg));
    const groups = groupAndSort(optionsVisible);

    for (const g of groups) {
      const section = el("div", { class: "trykk-pack", "data-trykk-pack": g.pack?.handle || g.pack?.id || "" });
      const packTitle = String(g.pack?.tittel || "").trim();
      if (packTitle) section.appendChild(el("div", { class: "trykk-pack-title", text: packTitle }));

      for (const opt of g.options) {
        const inputType = String(opt.inputtype || "none").toLowerCase();

        const box = el("div", {
          class: "trykk-box",
          "data-trykk-box": "1",
          "data-trykk-code": opt.kode || "",
          "data-input-type": inputType,
          "data-required": String(!!opt.pakrevd),
          "data-default-on": String(!!opt.default_on),
          "data-max-len": String(opt.makslengdetekst ?? ""),
          "data-min": String(opt.minverdi ?? ""),
          "data-max": String(opt.maxverdi ?? ""),
          "data-trykk-shopify-variant-id": String(
            opt?.trykkprodukt?.variant_id ||
            opt?.trykkvariant?.id ||
            opt?.trykkVariant?.id ||
            opt?.trykkvariant_id ||
            opt?.trykkvariantId ||
            ""
          ),
          "data-trykk-variant-sku": String(
            opt?.trykkprodukt?.variant_sku ||
            opt?.trykkvariant?.sku ||
            opt?.trykkVariant?.sku ||
            opt?.trykkvariant_sku ||
            opt?.trykkvariantSku ||
            ""
          )
        });

        const head = el("div", { class: "trykk-head" });

        const left = el("div", { class: "trykk-left" });
        const title = el("div", { class: "trykk-title", text: opt.tittel || opt.kode || "Trykk" });
        left.appendChild(title);

        const priceText = formatTrykkPrice(opt?.trykkprodukt?.price_text ?? opt?.trykkprodukt?.price_cents ?? "");
        if (priceText) left.appendChild(el("div", { class: "trykk-price", text: `+ ${priceText}` }));

        const toggleWrap = el("label", { class: "trykk-toggle" });
        const cb = el("input", { type: "checkbox", class: "js-trykk-enabled" });
        const cbTxt = el("span", { text: "Legg til" });
        const hint = el("span", { class: "trykk-hint js-trykk-hint", text: "" });
        toggleWrap.appendChild(cb);
        toggleWrap.appendChild(cbTxt);
        toggleWrap.appendChild(hint);

        head.appendChild(left);
        head.appendChild(toggleWrap);

        const meta = el("div", { class: "trykk-meta" });
        meta.appendChild(el("div", { class: "trykk-meta-label", text: "Antall trykk:" }));
        meta.appendChild(el("input", { class: "trykk-meta-input js-trykk-qty", type: "text", value: "0", readonly: "readonly" }));

        const extra = el("div", { class: "js-trykk-extra" });
        extra.appendChild(el("div", { class: "js-trykk-inputs" }));

        const help = el("div", { class: "trykk-help" });
        if (inputType === "none") help.textContent = "Ingen input – valget legges til i handlekurven.";
        else if (inputType === "text") help.textContent = "Skriv inn navn (en linje per trykk).";
        else if (inputType === "number_range") help.textContent = "Velg nummer (en linje per trykk).";
        else if (inputType === "name_number") help.textContent = "Skriv inn navn og velg nummer (en linje per trykk).";
        extra.appendChild(help);

        box.appendChild(head);
        box.appendChild(meta);
        box.appendChild(extra);

        section.appendChild(box);
      }

      mount.appendChild(section);
    }

    if (dbgEnabled()) log("Rendered TRYKK", { total: optionsAll.length, visible: optionsVisible.length });
  }

  async function sparkAddOrUpsert({ mainVariantId, q, trykkLines, mainSku }) {
    const mainPropsObj = { _trykk_enabled: "Ja", _trykk_qty: String(q), _trykk_source: "trykk_box" };
    await upsertMainAndTrykkManySpark({ mainSku, qAdd: q, mainVariantId, mainPropsObj, trykkLines });
  }

  async function shopifyAdd({ mainVariantId, q, trykkLines, mainSkuHint }) {
    const mainPropsObj = { _trykk_enabled: "Ja", _trykk_qty: String(q), _trykk_source: "trykk_box" };

    const items = [{ id: Number(mainVariantId), quantity: Number(q), properties: mainPropsObj }];

    for (const t of trykkLines) {
      if (!t.shopifyVariantId) throw new Error(`Mangler trykk-variant ID for kode=${t.code}.`);
      items.push({
        id: Number(t.shopifyVariantId),
        quantity: Number(q),
        properties: {
          _source: "trykk_box",
          _trykk_for: String(mainVariantId),
          _trykk_for_sku: String(mainSkuHint || ""),
          _trykk_code: String(t.code),
          _trykk_input_type: String(t.inputType || ""),
          _trykk_values: t.inputType === "none" ? "" : String(t.valuesJson || "")
        }
      });
    }

    await shopifyAddItems(items);
    triggerCartRefreshBestEffort();
  }

  function attachSubmitOnce() {
    if (document.__trykkSubmitAttached) return;
    document.__trykkSubmitAttached = true;

    document.addEventListener("submit", async (e) => {
      const f = e.target?.closest?.('form[action*="/cart/add"], product-form form');
      if (!f) return;

      syncFlikPolicy();
      const rootForBoxes = document.querySelector(ROOT_SEL);
      const enabledBoxes = Array.from((rootForBoxes ? rootForBoxes.querySelectorAll(BOX_SEL) : document.querySelectorAll(BOX_SEL)))
        .filter(b => b.querySelector(".js-trykk-enabled")?.checked);

      if (!enabledBoxes.length) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      try {
        // Ensure inventory cache is ready if needed (Spark mode)
        loadVariantInventoryCache();

        const mainVariantId = readVariantIdFromFormStrict(f);
        const q = getQty(findQtyInput(f.getAttribute("id") || ""));
        if (!mainVariantId || q <= 0) return;

        const trykkLines = [];

        for (const box of enabledBoxes) {
          const code = norm(box.dataset.trykkCode);
          const inputType = normLC(box.dataset.inputType || "none");

          const trykkSku = norm(box.dataset.trykkVariantSku);
          const trykkShopifyVariantId = Number(box.dataset.trykkShopifyVariantId || 0);

          const required = String(box.dataset.required || "false") === "true";

          let values = [];
          if (inputType !== "none") {
            values = collectValues(box, q);
            if (required) {
              const ok = validateValues(box, values);
              if (!ok.ok) { alert(ok.msg || "Ugyldig trykk-input."); return; }
            }
          }

          if (!code) continue;

          trykkLines.push({
            code,
            inputType,
            valuesJson: inputType === "none" ? "" : JSON.stringify(values),
            qtyAdd: q,
            sku: trykkSku,
            shopifyVariantId: trykkShopifyVariantId
          });
        }

        if (hasSpark()) {
          const mainSku = resolveSelectedVariantSkuStrict(f);

          if (dbgEnabled()) {
            log("SUBMIT (Spark clean v5)", {
              productId: getCurrentProductId(),
              variantId: mainVariantId,
              selectedOptions: getSelectedOptionsFromVariantSelects(),
              mainSku
            });
          }

          if (!mainSku) {
            throw new Error(
              "Kunne ikke mappe valgt variant til SKU for Spark. " +
              "Sjekk VariantInventory JSON (må inneholde sku + variantId/optioner)."
            );
          }

          await sparkAddOrUpsert({ mainVariantId, q, trykkLines, mainSku });
          return;
        }

        // B2C Shopify
        const root = document.querySelector(ROOT_SEL);
        const mainSkuHint = norm(root?.dataset?.mainVariantSku || root?.dataset?.mainSku);
        await shopifyAdd({ mainVariantId, q, trykkLines, mainSkuHint });
        window.location.href = "/cart";
      } catch (error) {
        errlog("ADD FEIL:", error);
        alert("Kunne ikke legge til trykk: " + (error?.message || "ukjent feil"));
      }
    }, true);
  }

  function initAll() {
    const root = document.querySelector(ROOT_SEL);
    if (!root) return;

    // Preload inventory cache (safe no-op if missing)
    loadVariantInventoryCache();

    render(root);

    const form = findMainForm();
    const qtyInput = findQtyInput();
    if (!form || !qtyInput) return;

    root.querySelectorAll(BOX_SEL).forEach(b => initBox(b, qtyInput));
    attachSubmitOnce();
  }

  function boot() {
    try { initAll(); }
    catch (e) { errlog("TRYKK boot error", e); }
  }

  document.addEventListener("DOMContentLoaded", boot);
  document.addEventListener("shopify:section:load", boot);
  document.addEventListener("shopify:section:reorder", boot);
  document.addEventListener("shopify:section:select", boot);

  document.addEventListener("variant:change", syncFlikPolicy);
  document.addEventListener("change", (e) => {
    if (e.target?.closest?.("variant-selects, [data-variant-selector]") || e.target?.name === "id") {
      syncFlikPolicy();
      requestAnimationFrame(syncFlikPolicy);
      setTimeout(syncFlikPolicy, 300);
    }
    const t = e.target;
    if (!t) return;
    if (t.matches('select[name="id"], input[name="id"], [data-variant-selector] select, [data-variant-selector] input')) {
      // Re-render trykk UI when variant changes (qty + input rows)
      boot();
    }
  });
})();

